import Vue from 'vue'
import { PatientState, PatientFilter, PatientRecord, PatientLogEntry } from './types'

const getDefaultState = () => {
    const state: PatientState = {
        phoneStatus: []
    }
    return state;
}

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        SET_PHONE_STATUS(state: PatientState, status: Array<any>) {
            state.phoneStatus = status;
        },
        RESET_STATE(state: PatientState) {
            Object.assign(state, getDefaultState())
        }
    },
    actions: {
        async getList({ rootState }, filter: PatientFilter) {
            // SET FORM DATA
            const params = new FormData();
            const entityId: string = rootState.auth.entityId;
            if (entityId) {
                params.append('entityId', entityId);
            }
            params.append('firstName', filter.firstName);
            params.append('lastName', filter.lastName);
            params.append('insurerId', filter.insurerId);
            params.append('insurerMemberId', filter.insurerMemberId);
            params.append('EMR_ID', filter.EMR_ID);
            params.append('withPhoneErrors', filter.withPhoneErrors ? String(filter.withPhoneErrors) : "");
            params.append('pageNumber', String(filter.pageNumber));
            params.append('rowsPerPage', String(filter.rowsPerPage));
            // GET PATIENTS ASYNC
            const { data } = await Vue.axios.post('patient/wmpc/list', params)
            //
            return data;
        },
        async getPatient({ rootState }, id: number) {
            // SET FORM DATA
            const params = new FormData();
            params.append('entityId', rootState.auth.entityId);
            params.append('patientId', String(id));
            // GET PATIENT ASYNC
            const { data } = await Vue.axios.post('patient/list/item', params)
            //
            return data;
        },
        async getContacts({ rootState }, id: number) {
            // SET FORM DATA
            const params = new FormData();
            params.append('patientId', String(id));
            // GET PATIENT ASYNC
            const { data } = await Vue.axios.post('patient/list/item/contact/list', params)
            //
            return data;
        },
        async getContactRelations({ rootState }) {
            // GET CONTACT RELATIONS ASYNC
            const { data } = await Vue.axios.post('patient/contact/relation/list')
            //
            return data;
        },
        async getContactTypes({ rootState }) {
            // GET CONTACT TYPES ASYNC
            const { data } = await Vue.axios.post('patient/contact/type/list')
            //
            return data;
        },
        async getProviders({ rootState }, id: number) {
            // SET FORM DATA
            const params = new FormData();
            params.append('patientId', String(id));
            // GET PATIENT ASYNC
            const { data } = await Vue.axios.post('patient/list/item/provider/list', params)
            //
            return data;
        },
        async getGaps({ rootState }, id: number) {
            // SET FORM DATA
            const params = new FormData();
            params.append('patientId', id.toString());
            // GET PATIENT GAPS ASYNC
            const { data } = await Vue.axios.post('patient/gap/list', params);
            //
            return data;
        },
        async getLogById({ rootState }, id: number) {
            // SET FORM DATA
            const params = new FormData();
            params.append('entityId', rootState.auth.entityId);
            params.append('patientId', String(id));
            // SET GAP LOG ASYNC
            const { data } = await Vue.axios.post('patient/log', params);
            //
            return data;
        },
        async getPhoneStatus({ commit, state }) {
            if (state.phoneStatus.length) return;
            // GET PHONE STATUS LIST ASYNC
            const { data } = await Vue.axios.post('patient/phonestatus/list');
            //
            commit("SET_PHONE_STATUS", data);
        },
        async saveLog({ rootState }, entry: PatientLogEntry) {
            // SET FORM DATA
            const params = new FormData();
            params.append('entityId', rootState.auth.entityId);
            params.append('patientId', String(entry.patientId));
            params.append('typeId', entry.typeId);
            params.append('comment', entry.comment ? entry.comment : "");
            // SAVE GAP LOG ASYNC
            const { data } = await Vue.axios.post('patient/log/save', params);
            //
            return data;
        },
        async saveContact({ rootState }, { contact, patientId }) {
            // SAVE PATIENT CONTACT TO DB
            const params = new FormData();

            params.append("patientId", patientId);
            if (contact.id) {
                params.append("id", contact.id);
            }
            params.append("typeId", contact.typeId);
            params.append("relationId", contact.relationId);
            params.append("firstName", contact.firstName);
            params.append("lastName", contact.lastName);
            params.append("phone", contact.phone);
            params.append("email", contact.email);
            const { data } = await Vue.axios.post("patient/list/item/contact/save", params);
            //
            return data;
        },
        async saveProvider({ rootState }, { provider, patientId }) {
            // SAVE PATIENT CONTACT TO DB
            const params = new FormData();

            params.append("patientId", patientId);
            params.append("id", provider.id);

            const { data } = await Vue.axios.post("patient/list/item/provider/save", params);
            //
            return data;
        },
        async archiveContact({ rootState }, { contact, patientId }) {
            // ARCHIVE CONTACT 
            const params = new FormData();
            params.append("id", contact.id);
            params.append("patientId", patientId);
            const { data } = await Vue.axios.post("patient/list/item/contact/archive", params);
            //
            return data;
        },
        async archiveProvider({ rootState }, { provider, patientId }) {
            // ARCHIVE CONTACT 
            const params = new FormData();
            params.append("id", provider.id);
            params.append("patientId", patientId);
            const { data } = await Vue.axios.post("patient/list/item/provider/remove", params);
            //
            return data;
        },
        async saveSystemLog({ rootState }, entry: PatientLogEntry) {
            // SET FORM DATA
            const params = new FormData();
            params.append('entityId', rootState.auth.entityId);
            params.append('patientId', String(entry.patientId));
            params.append('typeId', entry.typeId);
            params.append('comment', entry.comment ? entry.comment : "");
            // SAVE GAP LOG ASYNC
            const { data } = await Vue.axios.post('patient/log/save/system', params);
            //
            return data;
        },
        async emrIdExists({ rootState }, EMR_ID: string) {
            const params = new FormData();
            // SET FORM DATA
            params.append('entityId', rootState.auth.entityId);
            params.append('EMR_ID', EMR_ID);
            // CHECK EMR_ID ASYNC
            const { data } = await Vue.axios.post('patient/emr/id/exists', params);
            //
            return data.exists;
        },
        async save({ rootState }, patient: PatientRecord) {
            const params = new FormData();
            // SET FORM DATA
            if (patient.id) {
                params.append('id', String(patient.id));
            }
            params.append('entityId', rootState.auth.entityId);
            params.append('firstName', patient.firstName);
            params.append('lastName', patient.lastName);
            params.append('lastName2', patient.lastName2 ? patient.lastName2 : "");
            params.append('birthdate', patient.birth.date);
            params.append('gender', patient.gender);
            // ADDRESS
            params.append('addressLine1', patient.addressLine1 ? patient.addressLine1 : "");
            params.append('addressLine2', patient.addressLine2 ? patient.addressLine2 : "");
            params.append('city', patient.city ? patient.city : "");
            params.append('state', patient.state ? patient.state : "");
            params.append('zip', patient.zip ? patient.zip : "");
            params.append('countryId', patient.countryId ? patient.countryId : "");
            // INSURER
            params.append('insurerId', patient.insurerId);
            params.append('insurerMemberId', patient.insurerMemberId);
            params.append('contactName', patient.contactName);
            // PHONES
            params.append('mobilePhone', patient.phone.mobile ? patient.phone.mobile : "");
            params.append('mobilePhoneStatusId', patient.phone.mobile ? patient.phone.mobileStatusId : "NP");
            params.append('homePhone', patient.phone.home ? patient.phone.home : "");
            params.append('homePhoneStatusId', patient.phone.home ? patient.phone.homeStatusId : "NP");
            params.append('otherPhone', patient.phone.other ? patient.phone.other : "");
            params.append('otherPhoneStatusId', patient.phone.other ? patient.phone.otherStatusId : "NP");
            params.append('otherPhone2', patient.phone.other2 ? patient.phone.other2 : "");
            params.append('otherPhone2StatusId', patient.phone.other2 ? patient.phone.other2StatusId : "NP");
            //
            params.append('ipaNumber', patient.ipaNumber ? patient.ipaNumber : "");
            params.append('ipaName', patient.ipaName ? patient.ipaName : "");
            //
            params.append('email', patient.email ? patient.email : "");
            params.append('lang', patient.lang);
            params.append('EMR_ID', patient.EMR_ID ? patient.EMR_ID : "");
            params.append('pcpProviderId', patient.pcpProviderId ? patient.pcpProviderId : "")
            // SAVE PATIENT ASYNC
            const { data } = await Vue.axios.post('patient/save', params);
            //
            return data;
        }
    }
}
